/**
 * POS-I118 — PKR / USD choice for the public price list.
 * Pakistan visitors (useCountry) default to PKR, everyone else to USD; a manual switch is remembered.
 * Also loads the public catalogue so stored USD prices (pricingByCurrency) win over the derived value.
 */
import { useCallback, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import type { DisplayCurrency } from '@/lib/price-currency';

const STORAGE_KEY = 'pos.landing.currency';
const EVENT = 'pos-currency-change';

function readOverride(): DisplayCurrency | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'PKR' || v === 'USD' ? v : null;
  } catch {
    return null;
  }
}

function subscribe(cb: () => void): () => void {
  window.addEventListener('storage', cb);
  window.addEventListener(EVENT, cb);
  return () => {
    window.removeEventListener('storage', cb);
    window.removeEventListener(EVENT, cb);
  };
}

interface PublicCatalog {
  plans: Array<{
    pricingByCurrency?: { PKR: number; USD: number };
    addons: Array<{ key: string; pricingByCurrency?: { PKR: number; USD: number } }>;
  }>;
}

export function useDisplayCurrency(isPakistan: boolean) {
  const override = useSyncExternalStore(subscribe, readOverride, () => null);
  const currency: DisplayCurrency = override ?? (isPakistan ? 'PKR' : 'USD');

  const setCurrency = useCallback((c: DisplayCurrency) => {
    try {
      localStorage.setItem(STORAGE_KEY, c);
    } catch {
      /* ignore storage failures */
    }
    window.dispatchEvent(new Event(EVENT));
  }, []);

  const { data } = useQuery<PublicCatalog | null>({
    queryKey: ['landing', 'public-catalog'],
    retry: false,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      try {
        const res = await api.get<PublicCatalog>('/subscriptions/public/catalog');
        return res.data;
      } catch {
        return null;
      }
    },
  });

  const baseUsd = data?.plans?.[0]?.pricingByCurrency?.USD ?? null;
  const addonUsd: Record<string, number> = {};
  for (const a of data?.plans?.[0]?.addons ?? []) {
    if (a.pricingByCurrency) addonUsd[a.key] = a.pricingByCurrency.USD;
  }

  return { currency, setCurrency, baseUsd, addonUsd };
}
